import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Check, CalendarCheck, Shield, Repeat, Send, Plus, Minus, Loader2, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const BENEFITS = [
  'Controllo e manutenzione completa di tutti i serramenti',
  'Lubrificazione e regolazione professionale',
  'Sostituzione guarnizioni usurate',
  'Report dettagliato sullo stato dei serramenti',
  'Priorita\' nella prenotazione degli interventi',
  'Sconti riservati su ricambi e riparazioni',
];

export default function Abbonamento() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nome, setNome] = useState(user?.name?.split(' ')[0] || '');
  const [cognome, setCognome] = useState(user?.name?.split(' ').slice(1).join(' ') || '');
  const [email, setEmail] = useState(user?.email || '');
  const [telefono, setTelefono] = useState('');
  const [numFinestre, setNumFinestre] = useState(5);
  const [numPorte, setNumPorte] = useState(2);
  const [frequenza, setFrequenza] = useState('annuale');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!nome || !cognome || !email) { toast.error('Compila nome, cognome e email'); return; }
    setSubmitting(true);
    try {
      await api.post('/contact-form', {
        nome, cognome, email, telefono,
        num_finestre: numFinestre, num_porte: numPorte,
        frequenza, notes,
      });
      setSubmitted(true);
      toast.success('Richiesta inviata!');
    } catch {
      toast.error('Errore nell\'invio');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center" data-testid="abbonamento-success">
        <Card className="border-0 shadow-sm rounded-xl max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PartyPopper className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-extrabold text-slate-900 mb-2">Richiesta Inviata!</h2>
            <p className="text-sm text-slate-500 mb-6">
              Ti contatteremo al piu' presto per concordare il tuo piano di manutenzione personalizzato.
            </p>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
              onClick={() => navigate('/dashboard')}
              data-testid="back-dashboard-btn"
            >
              Torna alla Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="abbonamento-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-900">Manutenzione in Abbonamento</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Info Banner */}
        <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex items-center gap-3 mb-3">
              <Repeat className="w-6 h-6" />
              <h2 className="text-lg font-extrabold">Manutenzione Programmata</h2>
            </div>
            <p className="text-sm text-blue-100 leading-relaxed">
              Proteggi i tuoi serramenti con un piano di manutenzione regolare.
              1 o 2 interventi all'anno per mantenere tutto in perfette condizioni.
            </p>
          </div>
        </Card>

        {/* Benefits */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-5">
            <p className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-600" />
              Cosa include l'abbonamento
            </p>
            <ul className="space-y-2.5">
              {BENEFITS.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  {b}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Contact Form */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-5 space-y-5">
            <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-blue-600" />
              Contattaci per un preventivo personalizzato
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Nome *</Label>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} className="rounded-xl" data-testid="nome-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Cognome *</Label>
                <Input value={cognome} onChange={(e) => setCognome(e.target.value)} className="rounded-xl" data-testid="cognome-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Email *</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="rounded-xl" data-testid="email-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Telefono</Label>
                <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} type="tel" className="rounded-xl" data-testid="telefono-input" />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Quante finestre?</Label>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => setNumFinestre(Math.max(0, numFinestre - 1))}>
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center font-bold" data-testid="finestre-count">{numFinestre}</span>
                  <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => setNumFinestre(numFinestre + 1)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Quante porte?</Label>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => setNumPorte(Math.max(0, numPorte - 1))}>
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center font-bold" data-testid="porte-count">{numPorte}</span>
                  <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => setNumPorte(numPorte + 1)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Frequenza desiderata</Label>
              <RadioGroup value={frequenza} onValueChange={setFrequenza} className="flex gap-4">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="annuale" id="freq-1" data-testid="freq-annuale" />
                  <Label htmlFor="freq-1" className="text-sm cursor-pointer">1 volta/anno</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="semestrale" id="freq-2" data-testid="freq-semestrale" />
                  <Label htmlFor="freq-2" className="text-sm cursor-pointer">2 volte/anno</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Note o richieste particolari</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Es: Serramenti in legno, villa su 2 piani..."
                className="rounded-xl" rows={3} data-testid="notes-input" />
            </div>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-base font-semibold"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-form-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Invio in corso...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Invia Richiesta</>
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
